import React, { useCallback, useContext, useState } from 'react';
import {
  Box, HStack, Text, VStack,
} from '@gluestack-ui/themed';

import { AnimatedFigureValue } from './AnimatedFigureValue';
import { SparklineTooltipLayerContext } from './sparklineTooltipLayer';
import { DASHBOARD_MOTION } from './dashboardMotion';

/** Above the caption (z-index 0) so an open tooltip card paints over that line. */
const SPARKLINE_SLOT_Z = 8;

const FIGURE_UNIT_COLOR = '#98A2B3';

export type FigureBlockProps = {
  label: string;
  value: string;
  unit?: string;
  caption: string;
  tooFew?: boolean;
  dimmed?: boolean;
  testID?: string;
  sparkline?: React.ReactNode;
  /** When set, the headline count-up runs from the previous numeric value. */
  animateNumeric?: number | null;
  formatAnimated?: (value: number) => string;
  reduceMotion?: boolean;
  staggerIndex?: number;
};

/**
 * Admin analytics figure (no card): label, value, unit, caption; greys when sample is small.
 */
export function FigureBlock({
  label,
  value,
  unit,
  caption,
  tooFew = false,
  dimmed = false,
  testID = 'figure-block',
  sparkline,
  animateNumeric,
  formatAnimated,
  reduceMotion = false,
  staggerIndex = 0,
}: FigureBlockProps) {
  const valueColor = tooFew ? '$textDisabled' : '$textHeading';
  const opacity = dimmed ? 0.55 : 1;
  const showUnit = !!unit && value !== '—';
  const parentNotify = useContext(SparklineTooltipLayerContext);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const notifyTooltip = useCallback((open: boolean) => {
    setTooltipOpen(open);
    parentNotify(open);
  }, [parentNotify]);
  const figureZ = tooltipOpen ? 40 : undefined;

  return (
    <SparklineTooltipLayerContext.Provider value={notifyTooltip}>
      <Box
        opacity={opacity}
        testID={testID}
        py="$2"
        px="$3"
        flex={1}
        minWidth={0}
        position="relative"
        zIndex={figureZ}
        style={{ overflow: 'visible', elevation: figureZ ?? 0 }}
      >
        <VStack space="xs">
          <Text size="sm" color="$textSubtle">{label}</Text>
          <HStack alignItems="baseline" space="xs">
            {formatAnimated ? (
              <AnimatedFigureValue
                numericValue={animateNumeric ?? null}
                formatValue={formatAnimated}
                reduceMotion={reduceMotion}
                delayMs={staggerIndex * DASHBOARD_MOTION.sectionStaggerMs}
                color={valueColor}
                testID={`${testID}-value`}
              />
            ) : (
              <Text
                fontFamily="$heading"
                color={valueColor}
                testID={`${testID}-value`}
                sx={{
                  fontSize: 32,
                  lineHeight: 36,
                  fontWeight: '600',
                  _web: { fontVariantNumeric: 'tabular-nums' },
                }}
                style={{ fontVariant: ['tabular-nums'] }}
              >
                {value}
              </Text>
            )}
            {!!showUnit && (
              <Text
                testID={`${testID}-unit`}
                sx={{
                  fontSize: 13,
                  lineHeight: 18,
                  fontWeight: '500',
                  color: FIGURE_UNIT_COLOR,
                  _web: { fontVariantNumeric: 'tabular-nums' },
                }}
                style={{
                  fontVariant: ['tabular-nums'],
                  color: tooFew ? undefined : FIGURE_UNIT_COLOR,
                }}
                color={tooFew ? '$textDisabled' : undefined}
              >
                {unit}
              </Text>
            )}
          </HStack>
          {!!sparkline && (
            <Box
              width="100%"
              testID={`${testID}-sparkline`}
              position="relative"
              zIndex={SPARKLINE_SLOT_Z}
              style={{ overflow: 'visible', elevation: SPARKLINE_SLOT_Z }}
            >
              {sparkline}
            </Box>
          )}
          <Text
            size="xs"
            color="$textHint"
            testID={`${testID}-caption`}
            zIndex={0}
            style={{ elevation: 0 }}
          >
            {caption}
          </Text>
        </VStack>
      </Box>
    </SparklineTooltipLayerContext.Provider>
  );
}
